/**
 * Showroom Cards Component
 * Handles collapsible features on mobile and SavvyCal booking integration
 */
(function showroomCards() {
  const MOBILE_BREAKPOINT = 768;

  const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const debounce = (fn, delay) => {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const toggleFeatures = (toggle, collapse, translations) => {
    const collapseEl = collapse;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    const newState = !expanded;

    toggle.setAttribute('aria-expanded', String(newState));

    const textEl = toggle.querySelector('.showroom-card__toggle-text');
    if (textEl) {
      textEl.textContent = newState ? translations.lessInfo : translations.moreInfo;
    }

    if (prefersReducedMotion()) {
      collapseEl.style.height = newState ? 'auto' : '0px';
      return;
    }

    collapseEl.classList.add('is-animating');
    const endHeight = newState ? collapseEl.scrollHeight : 0;
    collapseEl.style.height = `${collapseEl.offsetHeight}px`;
    // eslint-disable-next-line no-unused-expressions
    collapseEl.offsetHeight; // Force reflow
    collapseEl.style.height = `${endHeight}px`;

    setTimeout(() => {
      collapseEl.classList.remove('is-animating');
      if (newState) {
        collapseEl.style.height = 'auto';
      }
    }, 240);
  };

  const initCard = (card, index, sectionId, translations) => {
    const collapse = card.querySelector('.showroom-card__collapse');
    const toggle = card.querySelector('.showroom-card__toggle');
    if (!collapse || !toggle) return;

    collapse.id = `showroom-features-${sectionId}-${index}`;
    toggle.setAttribute('aria-controls', collapse.id);

    const mobile = isMobile();
    toggle.setAttribute('aria-expanded', String(!mobile));
    if (mobile) collapse.style.height = '0px';

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      toggleFeatures(toggle, collapse, translations);
    });
  };

  const setupSavvyCalLinks = (savvycalOrigin) => {
    try {
      // Extract domain from origin URL for link matching
      const originUrl = new URL(savvycalOrigin);
      const domain = originUrl.hostname;

      document.querySelectorAll(`a[href*="${domain}"]`).forEach((link) => {
        link.setAttribute('data-savvycal-embed', '');
        link.removeAttribute('target');
        link.removeAttribute('rel');
      });
    } catch (e) {
      // Fallback to default domain if URL parsing fails
      document.querySelectorAll('a[href*="book.pergolux.co.uk"]').forEach((link) => {
        link.setAttribute('data-savvycal-embed', '');
        link.removeAttribute('target');
        link.removeAttribute('rel');
      });
    }
  };

  const handleBookingCompleted = (ev) => {
    const detail = ev.detail || {};
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'savvycal_booking_completed',
      booking_id: detail.id || '',
      booking_link: detail.link || '',
      booking_start_time: detail.startAt || '',
      booking_end_time: detail.endAt || '',
      attendee_email: detail.email || '',
      attendee_name: detail.displayName || '',
      attendee_phone: detail.phoneNumber || '',
      attendee_timezone: detail.timeZone || '',
      booking_url: detail.url || '',
    });

    window.dataLayer.push({
      event: 'showroom_booking_conversion',
      booking_link: detail.link || '',
    });
  };

  let bookingListenerRegistered = false;
  const registerBookingListener = () => {
    if (bookingListenerRegistered) return;
    window.addEventListener('savvycal.scheduled', handleBookingCompleted);
    bookingListenerRegistered = true;
  };

  const initShowroomCards = (container) => {
    // eslint-disable-next-line prefer-destructuring
    const sectionId = container.dataset.sectionId;
    const savvycalOrigin = container.dataset.savvycalOrigin || 'https://book.pergolux.co.uk';
    const translations = {
      moreInfo: container.dataset.labelMoreInfo || 'More Information',
      lessInfo: container.dataset.labelLessInfo || 'Less Information',
    };

    const cards = container.querySelectorAll('.showroom-card');
    cards.forEach((card, index) => initCard(card, index, sectionId, translations));

    setupSavvyCalLinks(savvycalOrigin);

    window.addEventListener(
      'resize',
      debounce(() => {
        cards.forEach((card) => {
          const collapse = card.querySelector('.showroom-card__collapse');
          const toggle = card.querySelector('.showroom-card__toggle');
          if (!collapse || !toggle) return;

          collapse.classList.remove('is-animating');
          if (!isMobile()) {
            toggle.setAttribute('aria-expanded', 'true');
            collapse.style.height = 'auto';
          }
        });
      }, 150)
    );
  };

  const setup = () => {
    document.documentElement.classList.add('has-js');

    registerBookingListener();

    // Initialize all showroom card sections on the page
    const containers = document.querySelectorAll('[data-showroom-cards]');
    containers.forEach(initShowroomCards);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
